import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { KoobSidebar, icons } from '@koob/ui';
import { openTripWizard } from '@koob/ui/src/features/tripWizard';
import { useApp, useAuth, useNotifications, useResetTripDesigner } from 'hooks';
import { useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { getDmcRequestAccess } from 'api/node/dmcRequestAccess';
import { permissionsEnum, stateEnum } from 'utils';
import NotificationType from 'constants/NotificationType';
import { settingsSections } from 'components/settingsAreas';

const canDisplayNotifications = (pathname, segment) => {
  return pathname.split('/').indexOf(segment) === -1;
};

const MainSidebar = () => {
  const { isAllowedTo, currentOrganizationId } = useApp();
  const resetContext = useResetTripDesigner();
  const { t } = useTranslation('mainSidebar');
  const { impersonating } = useAuth();
  const { pathname } = useLocation();
  const { notifications } = useNotifications();
  const [isValidTripDesignerRequest, setIsValidTripDesignerRequest] = useState(
    localStorage.getItem('tripDesignerRequestState') === stateEnum.VALIDATED,
  );

  const canDisplayTripNotification = canDisplayNotifications(
    pathname,
    'trip-designer',
  );

  const canDisplayBookingsNotifications = canDisplayNotifications(
    pathname,
    'bookings',
  );

  const { data: dmcRequest, reload: reloadDmcRequestAccess } = useAsync({
    promiseFn: getDmcRequestAccess,
  });

  useEffect(() => {
    if (impersonating) {
      reloadDmcRequestAccess();
    }
  }, [impersonating, reloadDmcRequestAccess]);

  const tripsNotificationsCounter = notifications.filter(
    (notification) =>
      [
        NotificationType.TRIP_HELP_REQUESTED,
        NotificationType.TRIP_HELP_ASSISTANT,
        NotificationType.TRIP_NEW_VERSION_AVAILABLE,
        NotificationType.TRIP_BOOKING_CONFIRMED,
      ].includes(notification.notificationType) && !notification.read,
  ).length;

  const bookingNotificationsCounter = notifications.filter(
    ({ notificationType, read }) =>
      /^(booking_.+|update_hotel|update_experience)$/.test(notificationType) &&
      !read,
  ).length;

  const connectionRequestNotificationsCounter = notifications.filter(
    ({ notificationType, read }) =>
      /^to_connection_request_/.test(notificationType) && !read,
  ).length;

  useEffect(() => {
    const isValid =
      dmcRequest?.data?.tripDesignerRequestState === stateEnum.VALIDATED;
    setIsValidTripDesignerRequest(isValid);
    localStorage.setItem(
      'tripDesignerRequestState',
      isValid ? stateEnum.VALIDATED : '',
    );
  }, [dmcRequest?.data?.tripDesignerRequestState]);

  // Build main navigation links
  const mainLinks = [];

  if (isAllowedTo('manageOrganizations')) {
    mainLinks.push({
      to: '/organizations',
      icon: icons.organizations,
      label: t('organizations'),
    });
  }

  if (isAllowedTo('readSelfOrganization')) {
    mainLinks.push({
      to: `/organizations/${currentOrganizationId}`,
      icon: icons.myOrganization,
      label: t('myOrganization'),
    });
  }

  if (isAllowedTo('readBookings')) {
    mainLinks.push({
      to: '/bookings/my-bookings',
      icon: icons.bookings,
      label: t('bookings'),
      alwaysExpanded: true,
      titleWhenExpanded: true,
      badge:
        canDisplayBookingsNotifications && bookingNotificationsCounter > 0
          ? bookingNotificationsCounter
          : null,
      children: [
        {
          to: '/bookings/my-bookings',
          label: t('booking'),
        },
        {
          to: '/bookings/partner-bookings',
          label: t('bookingPartners'),
          badge:
            bookingNotificationsCounter > 0
              ? bookingNotificationsCounter
              : null,
        },
        ...(isAllowedTo('manageBookings')
          ? [
              {
                to: '/bookings/new/hotel',
                icon: icons.search,
                label: t('hotelSearch'),
              },
            ]
          : []),
      ],
    });
  }

  mainLinks.push({
    to: '/hotels/catalog',
    icon: icons.hotels,
    label: t('hotels'),
  });

  mainLinks.push({
    to: '/experiences',
    icon: icons.experiences,
    label: t('experiences'),
  });

  if (isAllowedTo(permissionsEnum.READ_BOOKINGS)) {
    const tripDesignerLink = {
      to: !isValidTripDesignerRequest
        ? '/trip-designer/'
        : '/trip-designer/trips',
      icon: icons.tripDesigner,
      label: t('tripDesigner'),
      dataCy: 'trip-designer',
      badge:
        canDisplayTripNotification && tripsNotificationsCounter > 0
          ? tripsNotificationsCounter
          : null,
    };

    if (isValidTripDesignerRequest) {
      // Trip Designer is pinned open: children always visible, no chevron.
      tripDesignerLink.alwaysExpanded = true;
      tripDesignerLink.children = [
        {
          label: t('designTrip'),
          dataCy: 'design-trip',
          // Opens over the current page instead of navigating away.
          onClick: () => {
            resetContext();
            openTripWizard();
          },
        },
        {
          to: '/trip-designer/trips',
          label: t('trips'),
        },
        {
          to: '/trip-designer/library/trips',
          label: t('library'),
          badge:
            tripsNotificationsCounter > 0 ? tripsNotificationsCounter : null,
        },
      ];
    }

    mainLinks.push(tripDesignerLink);
  }

  const toConnectionRequestChildren = [
    {
      to: '/to-connection-request/connections',
      label: t('connections'),
    },
    {
      to: '/to-connection-request/bulk-edit',
      label: t('bulkEdit'),
    },
    {
      to: '/to-connection-request/models',
      label: t('connectionRequestModels'),
    },
    {
      to: '/to-connection-request/promotions',
      label: t('promotions'),
    },
  ];

  mainLinks.push({
    to: '/to-connection-request',
    icon: icons.distribution,
    label: t('connectionRequest'),
    badge:
      connectionRequestNotificationsCounter > 0
        ? connectionRequestNotificationsCounter
        : null,
    children: toConnectionRequestChildren,
  });

  mainLinks.push({
    to: '/allotments',
    icon: icons.allotments,
    label: t('allotments'),
  });

  mainLinks.push({
    to: '/night-rates',
    icon: icons.nightRates,
    label: t('nightRates'),
  });

  // One link, last, opens the settings modal on its first section.
  const settingsLinks = settingsSections(t, isAllowedTo).flatMap(
    (group) => group.links,
  );
  mainLinks.push({
    to: settingsLinks[0].to,
    icon: icons.settings,
    label: t('settingsModal.title'),
    activeMatch: settingsLinks.flatMap((link) => link.activeMatch),
    dataCy: 'settings',
  });

  return (
    <KoobSidebar
      sections={[{ title: null, links: mainLinks }]}
      footerLinks={[
        {
          label: t('userGuide'),
          icon: icons.supportCenter,
          href: 'https://support.koob.tech/en/',
          external: true,
        },
      ]}
      router={{ NavLink, useLocation }}
    />
  );
};

export default MainSidebar;
